import { useEffect, useRef, useState } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { Spinner, Typography } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import { api } from "../../../services/api/api";

interface AutoDiagPhotoLinkProps {
  submissionId: string;
}

/**
 * Ouvre la photo de pesée d'un enregistrement Auto Diag.
 *
 * Les octets passent par l'API et non par un lien vers le bucket : les objets y sont chiffrés en
 * SSE-C, dont la clé voyage dans les en-têtes de la requête — un navigateur qui suit un lien ne
 * peut pas la fournir. C'est le motif déjà utilisé pour les photos d'intervention et les captures
 * diag sécu.
 */
export default function AutoDiagPhotoLink({ submissionId }: AutoDiagPhotoLinkProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const objectUrls = useRef<string[]>([]);

  useEffect(
    () => () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.current = [];
    },
    []
  );

  const open = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setHasFailed(false);
    try {
      const buffer = await api.getPhotos(
        `admin/auto-diag/submissions/${submissionId}/photo`
      );
      const objectUrl = URL.createObjectURL(new Blob([buffer], { type: "image/jpeg" }));
      // Conservée jusqu'au démontage : la révoquer tout de suite fermerait l'onglet qu'on vient
      // d'ouvrir.
      objectUrls.current.push(objectUrl);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      setHasFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={isLoading}
      className="inline-flex items-center gap-1 text-sky-500 hover:underline disabled:opacity-60"
    >
      {isLoading ? (
        <Spinner className="w-4" />
      ) : (
        <ArrowTopRightOnSquareIcon className="w-4" />
      )}
      <Typography variant="small">
        {hasFailed ? t("admin:autoDiag.photoFailed") : t("admin:autoDiag.viewPhoto")}
      </Typography>
    </button>
  );
}
